// @ts-check
/* global Tygh $ module require*/
'use_strict';

import { resize_last_dialog } from '../../tv_utils/utils.js';

declare var $;

/**
 * UI object to be used for config number of design validation.
 *
 * @property {Object} elements Object to house HTML elements that get interacted with.
 * @property {number} order_quantity The quantity of the order.
 * @property {boolean} do_validation Flag to determine if the image validation should run.
 * @property {function|null} callback Run the function if provided.
 */
export class ImageValidation {

    /**
     * @var {[key: string]: any} elements is a collection of elements.
     * @var {number} order_quantity is the quantity of the order.
     * @var {boolean} do_validation Flag to determine if the image validation should run.
     * @var {function|null} callback Run the function if provided.
     * @var {boolean} modal_is_loading flag to indicate whether the modal is loading.
     * @var {MutationObserver} open_observer Observes when modal is opened.
     * @var {boolean} reconfig Flag to determine if this is a reconfiguration.
     */
    elements: {[key: string]: any} = {};
    order_quantity: number = 0;
    max_number_designs: number = 0;
    do_validation: boolean = false;
    callback: Function|null;
    modal_is_loading: boolean;
    open_observer: MutationObserver;
    reconfig: boolean = false;

    /**
     * Class to validate number of images and quantity of designs.
     *
     * @param {function|null} callback function to run when submit button is clicked.
     */
    constructor(callback, reconfig = false) {
        this.callback = callback;
        this.reconfig = reconfig;

        this.bind_all();
    }

    /**
     * Binds the open button and the rest of the input elements.
     *
     * @return {void}
     */
    bind_all() {
        // Check to make sure order quantity element exists because it will not if it is a hardware only product
        // and in that case we don't need to worry about image validation.
        if (!document.getElementById('js-image-validation-order-quantity')) {
            return;
        }

        this.elements.open = document.getElementsByClassName('js-update-quantity-designs-button');
        if (!this.elements.open) {
            return;
        }

        this.do_validation = true;

        for (const open_buttons of this.elements.open) {
            open_buttons.addEventListener('click', this.reset_form.bind(this, false), false);
        }

        this.bind();
    }

    /**
     * Binds all of the input elements of the modal.
     *
     * @return {void}
     */
    bind() {
        // Number of Images validation
        this.elements.number_of_designs = document.querySelector('.js-number-of-designs');
        this.elements.number_of_designs.addEventListener('change', this.update_number_of_designs.bind(this), false);

        // Observe for modal opening
        this.elements.modal = document.getElementById('js-quantity-designs-modal');
        this.open_observer = new MutationObserver(this.open_modal_callback.bind(this));
        this.open_observer.observe(this.elements.modal, {childList: true, attributes: false});

        this.order_quantity = parseInt((<HTMLInputElement> document.getElementById('js-image-validation-order-quantity')).value, 10);
        this.max_number_designs = parseInt((<HTMLInputElement> document.getElementById('js-max-number-designs')).value, 10);

        this.bind_design_quantity_fields();
        this.bind_artwork_modal_values();

        for (const design of this.elements.design_quantities) {
            design.addEventListener('change', this.design_quantity_event_update.bind(this));
        }
    }

    /**
     * Callback for when the modal has opened.
     *
     * @return {void}
     */
    open_modal_callback(): void {
        if (
            this.modal_is_loading &&
            this.elements.modal.parentElement.classList.contains('ui-dialog') &&
            this.elements.modal.parentElement.style.display !== 'none'
        ) {
            this.modal_is_loading = false;
            document.body.classList.remove('no-scroll');

            // Add space for bottom buttons
            const js_upload_modal_content = document.querySelector('#js-artwork-modal-content') as HTMLElement;

            js_upload_modal_content.style.marginBottom = '0px';
            resize_last_dialog();
            js_upload_modal_content.style.marginBottom = `80px`;                

            for (const container of this.elements.modal.getElementsByClassName('object-container')) {
                container.style.margin = '0 5px 5px 0';
            }
            // Add Mobile Height Fix
            if (this.order_quantity > 1) {
                let design_modal = document.getElementsByClassName('ui-dialog');
                let last_modal = design_modal[design_modal.length - 1];
                last_modal.classList.add('dialog-height-fix');
            }
        }
    }

    /**
     * Binds all of the design_quantities fields and adjusts values if a field was removed.
     *
     * @return {void}
     */
    bind_design_quantity_fields() {
        this.elements.design_quantities = document.querySelectorAll('.js-quantity-of-design');

        let total_qty = 0;
        for (const design of this.elements.design_quantities) {
            total_qty += parseInt(design.value, 10);
        }

        const difference = this.order_quantity - total_qty;
        /**
         * If the total quantity of our design fields is less than the order quantity we need to increase
         * the total quantity of designs.  We will just take the difference and add it to the first field.
         */
        if (difference > 0) {
            const first_element = this.elements.design_quantities[0];
            first_element.value = parseInt(first_element.value, 10) + difference;
        }

        if (this.elements.design_quantities.length === this.order_quantity) {
            for (const design of this.elements.design_quantities) {
                design.value = 1;
            }
        }
    }

    /**
     * 
     * @return {void}
     */
    bind_artwork_modal_values() {
        this.elements.tv_design_options_content = document.getElementsByClassName('tv-design-options-content')[0];
        this.elements.tv_multiple_design_content = document.getElementsByClassName('tv-multiple-design-content')[0];
        this.elements.tv_multiple_design_question = document.getElementsByClassName('tv-multiple-design-question')[0];
        this.elements.quantity_designs_modal_opener = document.getElementById('js-quantity-designs-modal-opener');
                
        // "Do you have different artwork?" buttons
        this.elements.skip_multi_design_button = document.getElementById('js-skip-multi-design-button');
        this.elements.proceed_multi_design_button = document.getElementById('js-proceed-multi-design-button');
        if (this.elements.skip_multi_design_button) {
            this.elements.skip_multi_design_button.addEventListener('click', () => {
                if (this.reconfig) {
                    this.close_modal_and_run_callback();
                } else {
                    this.skip_multi_design();
                }
            });
        }
        if (this.elements.proceed_multi_design_button) {
            this.elements.proceed_multi_design_button.addEventListener('click', this.proceed_multi_design.bind(this));
        }

        // Skip to Design Options Button
        this.elements.skip_to_design_button = document.getElementById('js-skip-to-design-options');
        if (this.elements.skip_to_design_button) {
            this.elements.skip_to_design_button.addEventListener('click', () => {
                if (this.reconfig) {
                    this.close_modal_and_run_callback();
                } else {
                    this.skip_to_design();
                }
            });
        }

        // Go Back To Multiple Designs Button
        this.elements.design_go_back_button = document.getElementById('js-design-go-back-button');

        if (this.elements.design_go_back_button) {
            this.elements.design_go_back_button.addEventListener('click', this.design_go_back.bind(this));
        }

        // Step number buttons.
        const step_inputs = document.querySelectorAll('.js-number-of-designs-modal-step-number-input');

        for (const step_number_button of step_inputs) {
            step_number_button.addEventListener('click', this.update_quantity_step_input.bind(this), false);
        }

        this.elements.submit = document.getElementById('js-update-number-of-designs-button');
        if (this.callback) {
            this.elements.submit.addEventListener('click', this.submit.bind(this), false);
        }
    }

    /**
     * Handles step input adjustments for both the number of design and design quantity fields.
     *
     * @param {Event} event The event from the element that changed.
     * @returns {void}
     */
    update_quantity_step_input(event) {
        // The data attributes from the button.
        const step_data = event.target.dataset;
        const field_id = step_data.fieldId;
        const step_type = step_data.stepType;
        const step_value = parseInt(step_data.stepValue, 10);

        // Select the number input by using the field_id.
        const number_input = <HTMLInputElement> document.getElementById(field_id);

        // Determine if the adjustment value should be positive or negative based on the step type.
        const adjustment_value = (step_type === 'add') ? Math.abs(step_value) : -Math.abs(step_value);

        // Calculate the updated value with the current input value and the adjustment value.
        const updated_value = parseInt(number_input.value, 10) + adjustment_value;

        // Check the updated value against the input min and max attributes.
        if (updated_value < parseInt(number_input.min, 10) ||
            updated_value > parseInt(number_input.max, 10)
        ) {
            return;
        }

        // Set the new updated value on the number input.
        number_input.value = updated_value.toString();

        if (number_input.classList.contains('js-number-of-designs')) {
            this.update_number_of_designs();
        } else if (number_input.classList.contains('js-quantity-of-design')) {
            this.update_design_quantity(number_input);
        }

    }

    /**
     * Update the number input from the step button and add or remove design quantity fields.
     *
     * @returns {void}
     */
    update_number_of_designs() {
        this.elements.number_of_designs.setCustomValidity('');
        let design_limit = 0;

        if (parseInt(this.elements.number_of_designs.value, 10) > this.order_quantity) {
            design_limit = this.order_quantity;
        }

        if (parseInt(this.elements.number_of_designs.value, 10) > this.max_number_designs) {
            design_limit = this.max_number_designs;
        }

        if (design_limit > 0) {
            this.elements.number_of_designs.setCustomValidity(
                'Number of Designs may not be greater than ' + design_limit
            );
            this.elements.number_of_designs.reportValidity();
            this.elements.number_of_designs.value = design_limit;
        }

        const adjustment_value = parseInt(this.elements.number_of_designs.value, 10)
            - this.elements.design_quantities.length;

        // Add or remove quantity of design field.
        if (adjustment_value < 0) {
            this.remove_design_quantity_fields(Math.abs(adjustment_value));
        } else if (adjustment_value > 0) {
            this.get_design_quantity_html(adjustment_value);
        }

        if (this.elements.number_of_designs.value > 1) {
            document.getElementById('js-update-number-of-designs-button').classList.remove('tv-display__none');
        } else {
            document.getElementById('js-update-number-of-designs-button').classList.add('tv-display__none');
        }
    }

    /**
     * Removes design quantity input fields from the modal.
     *
     * @param {number} fields_to_remove the number of design quantity fields that need to be removed.
     * @return {void}
     */
    remove_design_quantity_fields(fields_to_remove) {
        const design_containers = document.querySelectorAll('.js-quantity-of-design-container');
        let last_index = design_containers.length - 1;

        for (let i = 0; i < fields_to_remove; i += 1) {
            design_containers[last_index].remove();
            last_index -= 1;
        }

        this.bind_design_quantity_fields();
        $.ceDialog('get_last').ceDialog('reload');
    }

    /**
     * Adds design quantity fields to the modal.
     *
     * @param {Object} data html data for the design quantity fields to add.
     * @return {void}
     */
    add_design_quantity_fields(data) {
        const designs_list = document.getElementById('js-quantity-of-designs-list');
        for (const html_string of data.html_strings) {
            const container = document.createElement('div');
            container.classList.add('js-quantity-of-design-container');
            container.innerHTML = html_string.trim();
            designs_list.appendChild(container);

            const design_input = (<HTMLInputElement> container.querySelector('.js-quantity-of-design'));
            design_input.addEventListener('change', this.design_quantity_event_update.bind(this));

            container.dataset.id = design_input.name;

            const step_inputs = container.querySelectorAll('.js-number-of-designs-modal-step-number-input');

            for (const step_number_button of step_inputs) {
                step_number_button.addEventListener('click', this.update_quantity_step_input.bind(this), false);
            }
        }

        this.bind_design_quantity_fields();

        $.ceDialog('get_last').ceDialog('reload');
    }

    /**
     * Retrieves html to create more design quantity fields.
     *
     * @param {number} fields_to_add the number of design quantity fields to add to the form.
     * @returns {void}
     */
    get_design_quantity_html(fields_to_add) {
        const data = {
            'current_key': this.elements.design_quantities.length,
            'max_qty': this.order_quantity,
            'fields_to_add': fields_to_add
        };
        $.ceAjax(
            'request',
            'index.php?dispatch=tv_configurator.quantity_of_design_html',
            {
                'full_render': true,
                'method': 'POST',
                'caching': false,
                'data': data,
                'callback': this.add_design_quantity_fields.bind(this)
            }
        );
    }

    /**
     * Event handler the for the design quantity field change event.
     *
     * @param {Event} e The event from the element that changed.
     * @returns {void}
     */
    design_quantity_event_update(e) {
        const element = e.target || e.srcElement;

        this.update_design_quantity(element);
    }

    /**
     * Validates that the quantity of designs total is not greater than the order quantity,
     * if it is then we reset it back to a valid number and show an error message.
     *
     * @param {HTMLInputElement} number_input Number input element that was updated.
     * @returns {void}
     */
    update_design_quantity(number_input) {
        number_input.setCustomValidity('');

        let total_of_designs = 0;
        for (const design of this.elements.design_quantities) {
            total_of_designs += parseInt(design.value, 10);
        }

        // Make sure the sum of the "Quantity of Design" fields matches the total order quantity.
        if (total_of_designs > this.order_quantity) {
            number_input.setCustomValidity(
                'The sum of the Quantity of Designs cannot be greater than ' + this.order_quantity
            );
            number_input.reportValidity();
            const difference = total_of_designs - this.order_quantity;
            number_input.value = (parseInt(number_input.value, 10) - difference).toString();
        }

    }

    /**
     * Validates that the image selections for the current configuration are valid.
     *
     * @returns {boolean} flag for whether design quantity numbers and number of designs is valid.
     */
    validate_images() {
        let image_totals = 0;

        // Get the sum of all the "Quantity of Design" fields.
        for (const image of this.elements.design_quantities) {
            const value = parseInt(image.value, 10);
            if (value <= 0) {
                image.reportValidity();
                return false;
            }
            image_totals = image_totals + parseInt(image.value, 10);
        }

        this.elements.number_of_designs.setCustomValidity('');
        let design_limit = 0;

        if (parseInt(this.elements.number_of_designs.value, 10) > this.order_quantity) {
            design_limit = this.order_quantity;
        }

        if (parseInt(this.elements.number_of_designs.value, 10) > this.max_number_designs) {
            design_limit = this.max_number_designs;
        }

        if (design_limit > 0) {
            this.elements.number_of_designs.setCustomValidity(
                'Number of Designs may not be greater than ' + design_limit
            );
            this.elements.number_of_designs.reportValidity();
            return false;

        }

        // Make sure the sum of the "Quantity of Design" fields matches the total order quantity.
        if (image_totals !== this.order_quantity) {
            this.elements.number_of_designs.setCustomValidity(
                'The sum of the Quantity of Designs fields must equal ' + this.order_quantity
            );
            this.elements.number_of_designs.reportValidity();
            return false;

        }

        return true;
    }

    skip_to_design() {
        this.elements.tv_design_options_content.classList.remove('tv-display__none');
        this.elements.tv_multiple_design_content.classList.add('tv-display__none');

        resize_last_dialog();

    }

    /**
     * Go-Back button returns to Multiple Design div from Design Options div
     * 
     * @returns {void}
     */
    design_go_back() {
        this.elements.tv_design_options_content.classList.add('tv-display__none');
        this.elements.tv_multiple_design_question.classList.remove('tv-display__none');

        resize_last_dialog();

    }

    /**
     * Skips Multiple Design div and goes directly to Design Options div
     * 
     * @returns {void}
     */
    skip_multi_design() {
        this.elements.tv_design_options_content.classList.remove('tv-display__none');
        this.elements.tv_multiple_design_content.classList.add('tv-display__none');
        this.elements.tv_multiple_design_question.classList.add('tv-display__none');

        // Remove height fix
        let design_modal = document.getElementsByClassName('ui-dialog');
        let last_modal = design_modal[design_modal.length - 1];
        last_modal.classList.remove('dialog-height-fix');

        resize_last_dialog();
    }

    /**
     * Proceeds to Multiple Design div
     * 
     * @returns {void}
     */
    proceed_multi_design() {
        this.elements.tv_design_options_content.classList.add('tv-display__none');
        this.elements.tv_multiple_design_content.classList.remove('tv-display__none');
        this.elements.tv_multiple_design_question.classList.add('tv-display__none');

        // Remove height fix
        let design_modal = document.getElementsByClassName('ui-dialog');
        let last_modal = design_modal[design_modal.length - 1];
        last_modal.classList.remove('dialog-height-fix');

        resize_last_dialog();
    }

    /**
     * Checks that the image and quantity fields are valid and runs the callback
     * function if it is.
     *
     * @param {Event} e for the clicked target.
     * @returns {void}
     */
    submit(e) {
        if (!this.validate_images()) {
            e.preventDefault();
            e.stopPropagation();
            return;
        }
        this.callback();
    }

    /**
     * Re-renders the modal form to the default values.
     *
     * @param {boolean} modal_already_open checks if modal has already been opened
     * @returns {void}
     */
    reset_form(modal_already_open = false) {
        this.modal_is_loading = true;
        document.body.classList.add('no-scroll');
        const image_validation = JSON.parse((<HTMLInputElement> document.getElementById('js-num-of-designs-default')).value);
        const session_id = (<HTMLInputElement> document.getElementById('session_id')).value;
        const product_id = (<HTMLInputElement> document.getElementById('product_id')).value;
        const enable_design_tool = (<HTMLInputElement> document.getElementById('js-enable-design-tool')).value;
        const enable_difu = (<HTMLInputElement> document.getElementById('js-enable-difu')).value;
        const recommended_design_option = (<HTMLInputElement> document.getElementById('js-recommended-design-option')).value;
        const smart_code = (<HTMLInputElement> document.getElementById('js-configuration-smart-code')).value;
        const no_template_upload = (<HTMLInputElement> document.getElementById('js-no-template-upload')).value;
        const wishlist = <HTMLInputElement> document.getElementById('js-wishlist-id');
        const wishlist_id = wishlist ? wishlist.value : '';

        $.ceAjax(
            'request',
            'index.php?dispatch=tv_configurator.rerender_quantity_designs',
            {
                'result_ids': 'js-quantity-designs-modal',
                'full_render': true,
                'method': 'POST',
                'caching': false,
                'data': {
                    'image_validation': image_validation,
                    'session_id': session_id,
                    'product_id': product_id,
                    'enable_design_tool': enable_design_tool,
                    'enable_difu': enable_difu,
                    'recommended_design_option': recommended_design_option,
                    'smart_code' : smart_code,
                    'no_template_upload' : no_template_upload,
                    'wishlist_id': wishlist_id 
                },
                'message': 'Loading Designs',
                'callback': this.open_form.bind(this, modal_already_open)
            }
        );
    }

    /**
     * Clicks the opener action for the modal then binds the modal elements
     * Hides and shows modal content depending on order_quantity 
     *
     * @param {boolean} modal_already_open checks if modal has already been opened
     * @returns {void}
     */
    open_form(modal_already_open) {
        this.bind();
        let no_template_upload = "0";
        let number_of_designs_value = (<HTMLInputElement>this.elements.number_of_designs).value;
        if (document.getElementById('js-no-template-upload-modal')) {
            no_template_upload = (<HTMLInputElement>document.getElementById('js-no-template-upload-modal')).value;
        }
        if (this.order_quantity > 1 && number_of_designs_value && modal_already_open == false) {
            // Hide and show Design Options and Multiple Design content as needed
            this.elements.tv_design_options_content.classList.add('tv-display__none');
            this.elements.tv_multiple_design_content.classList.add('tv-display__none');
            this.elements.tv_multiple_design_question.classList.remove('tv-display__none');
            this.elements.design_go_back_button.classList.remove('tv-display__none');
        } else {
            // Hide and show Design Options and Multiple Design content as needed
            this.elements.tv_design_options_content.classList.remove('tv-display__none');
            this.elements.tv_multiple_design_content.classList.add('tv-display__none');
            this.elements.tv_multiple_design_question.classList.add('tv-display__none');
            if (modal_already_open == false) {
                this.elements.design_go_back_button.classList.add('tv-display__none');
            }
        }
        const enable_design_tool = (<HTMLInputElement> document.getElementById('js-enable-design-tool')).value;
        if ((no_template_upload === "1" || enable_design_tool === "0") && modal_already_open === true) {
            const session_id = (<HTMLInputElement> document.getElementById('session_id')).value;
            document.getElementById('js-cart-artwork-upload-modal-opener-' + session_id).click();
        } else {
            this.elements.quantity_designs_modal_opener.click();
        }
    }

    /**
     * Closes the current modal and executes the callback function.
     * 
     * @returns 
     */
    close_modal_and_run_callback() {
        const dialog = $.ceDialog('get_last')[0];
        if (dialog) {
            $.ceDialog('get_last').ceDialog('close');
        }

        return this.callback();
    }
}
